import axios, { AxiosInstance } from 'axios';

import { CONFIG } from '~/config';

import { ApiResponse } from '~/types';

import { store } from '../store/index';
import { logOut } from '~/actions';

export class ApiRestClient {
  private readonly client: AxiosInstance;

  constructor(baseURL?: string) {
    this.client = axios.create({
      baseURL,
      headers: { 'X-Tripenger-Caller': 'tripenger-fe' },
    });
    this.client.interceptors.request.use(config => {
      if (typeof window !== 'undefined') {
        config.headers['X-Tripenger-Page'] = window.location.pathname;
      }
      return config;
    });
  }

  async post<T, R>(path: string, data: T): Promise<ApiResponse<R>> {
    const response = await this.client.post(path, data);

    return {
      data: response.data as R,
      status: response.status,
    };
  }

  async get<R>(path: string): Promise<ApiResponse<R>> {
    const response = await this.client.get(path);

    return {
      data: response.data as R,
      status: response.status,
    };
  }

  async put<T, R>(path: string, data: T): Promise<ApiResponse<R>> {
    const response = await this.client.put(path, data);

    return {
      data: response.data as R,
      status: response.status,
    };
  }

  async delete<R>(path: string): Promise<ApiResponse<R>> {
    const response = await this.client.delete(path);

    return {
      data: response.data as R,
      status: response.status,
    };
  }

  async patch<T, R>(path: string, data: T): Promise<ApiResponse<R>> {
    const response = await this.client.patch(path, data);

    return {
      data: response.data as R,
      status: response.status,
    };
  }


  withAuth() {
    this.client.interceptors.request.use(
      config => {
        const authToken = localStorage.getItem(CONFIG.app.JWTStorageKey);
        if (authToken) {
          /* eslint-disable no-param-reassign */
          config.headers.Authorization = `Bearer ${authToken}`;
          /* eslint-enable no-param-reassign */
        } else {
          delete config.headers.Authorization;
        }

        return config;
      },
      error => {
        return Promise.reject(error);
      },
    );

    this.client.interceptors.response.use(
      response => {
        const authHeader = response.headers.authorization;
        if (authHeader && authHeader.startsWith('Bearer ')) {
          const newToken = authHeader.split(' ')[1];
          if (newToken) {
            localStorage.setItem(CONFIG.app.JWTStorageKey, newToken);
          }
        }
        return response;
      }
    );

    return this;
  }

  withRedirect() {
    // Add a response interceptor to handle 401 and 403 errors
    this.client.interceptors.response.use(
      response => {
        return response;
      },
      error => {
        const { status } = error.response;

        if (status === 401 || status === 403) {
          store.dispatch(logOut());
        }

        return Promise.reject(error);
      },
    );

    return this;
  }
}

export default new ApiRestClient().withAuth().withRedirect();
export const ExternalRestClient = new ApiRestClient().withAuth();
